import { supabase } from '@/integrations/supabase/client';
import { hasActiveSession } from '@/utils/authCheck';
import { logger } from '@/utils/logger';

/**
 * Self-healing guard for stale / unverifiable Supabase session JWTs.
 *
 * supabase-js `getSession()` returns the locally persisted session WITHOUT
 * re-validating its signature against the project — it only checks expiry. So a
 * token that is unexpired but signed by a rotated-out (or wrong-project) signing
 * key is replayed on every request and PostgREST rejects each one with:
 *
 *   { code: 'PGRST301', message: 'No suitable key or wrong key type',
 *     details: 'None of the keys was able to decode the JWT' }
 *
 * The existing AuthRecovery / emergencyAuthFix only clear sessions with an
 * INVALID STRUCTURE or EXPIRY, so this case slips past it and the whole app
 * bricks with a silent 401 storm (and edge functions surface it as a CORS /
 * "Service Connection Failed" error, because verify_jwt rejects the bearer at
 * the gateway before the function's CORS headers are sent).
 *
 * Two distinct failure modes, two distinct recoveries:
 *
 *  - UNRECOVERABLE (PGRST301 / "cannot decode"): the token's signing key is
 *    gone — a refresh would mint another token signed by the same dead key, so
 *    there is nothing to do but clear the session and route to sign-in.
 *
 *  - EXPIRED (PGRST303 / "JWT expired"): the access token simply aged out. The
 *    refresh token (lifetime ~weeks) is almost always still valid, so the right
 *    move is to REFRESH — not sign out. F-107 (UAT 2026-06-13): the old guard
 *    lumped "jwt expired" in with the decode failures and force-signed-out on
 *    it, which is exactly what bricked Clayton's long-open tab (Sentry showed a
 *    PGRST303 storm on /auth right before his chat 401'd). Refreshing here
 *    primes the next request/refetch and keeps the user signed in.
 */

let handled = false;

/** PGRST301 / signature-decode failures — the signing key is gone, refresh can't help. */
const UNRECOVERABLE_HINTS = [
  'no suitable key or wrong key type',
  'none of the keys was able to decode',
  'jwserror',
];

const DELETED_USER_HINTS = ['sub claim in jwt does not exist'];

/** PGRST303 / plain expiry — recoverable by refreshing the (still-valid) refresh token. */
const EXPIRED_HINTS = ['jwt expired'];

function blobOf(error: unknown): { code?: string; blob: string } {
  if (!error || typeof error !== 'object') return { blob: '' };
  const e = error as { code?: string; message?: string; details?: string };
  return { code: e.code, blob: `${e.message ?? ''} ${e.details ?? ''}`.toLowerCase() };
}

export function isDeletedUserError(error: unknown): boolean {
  const { code, blob } = blobOf(error);
  if (code === 'user_not_found') return true;
  return DELETED_USER_HINTS.some((hint) => blob.includes(hint));
}

export function isUnrecoverableSessionError(error: unknown): boolean {
  const { code, blob } = blobOf(error);
  if (code === 'PGRST301') return true;
  if (isDeletedUserError(error)) return true;
  return UNRECOVERABLE_HINTS.some((hint) => blob.includes(hint));
}

export function isExpiredSessionError(error: unknown): boolean {
  const { code, blob } = blobOf(error);
  if (code === 'PGRST303') return true;
  return EXPIRED_HINTS.some((hint) => blob.includes(hint));
}

/** Back-compat union predicate (either failure mode warrants a recovery attempt). */
export function isInvalidSessionError(error: unknown): boolean {
  return isUnrecoverableSessionError(error) || isExpiredSessionError(error);
}

async function clearDeadSession(): Promise<void> {
  try {
    // scope: 'local' — the bad token would be rejected by the server anyway;
    // we only need to drop it from local storage and emit SIGNED_OUT.
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('sb-') && key.endsWith('-auth-token'))
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      /* storage unavailable — nothing more we can do */
    }
  }
}

/**
 * Fire-and-forget. Only acts when a session is actually persisted (a plain 401
 * with no stored token is the normal logged-out case and is left alone). Runs
 * at most once per page lifetime to avoid a sign-out / refresh loop while the
 * 401 storm is still in flight; a successful refresh or sign-in re-arms it (see
 * the onAuthStateChange handler below).
 */
export async function handleInvalidSessionError(error: unknown): Promise<void> {
  if (typeof window === 'undefined') return;
  if (handled) return;
  if (!hasActiveSession()) return;

  // Expired access token → try to refresh BEFORE giving up the session.
  if (isExpiredSessionError(error)) {
    handled = true;
    logger.warn(
      '[auth] Access token expired (PGRST303). Attempting a silent refresh before ' +
        'signing out — the refresh token is usually still valid (long-open tab).',
    );
    try {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !data.session) {
        logger.warn(
          '[auth] Refresh after expiry failed — clearing session and routing to sign-in.',
        );
        await clearDeadSession();
      }
      // On success: TOKEN_REFRESHED fires, re-arms `handled`, and the next
      // request/refetch carries the fresh token. Nothing else to do here.
    } catch {
      await clearDeadSession();
    }
    return;
  }

  // Unrecoverable (key cannot decode) → nothing to refresh; clear and re-auth.
  if (!isUnrecoverableSessionError(error)) return;

  handled = true;
  logger.warn(
    '[auth] Stored Supabase session is no longer valid (token cannot be decoded, or its user no ' +
      'longer exists). Clearing the dead session and returning to sign-in.',
  );
  await clearDeadSession();
}

type GetUserFn = typeof supabase.auth.getUser;

export function watchDeletedUser(auth: { getUser: GetUserFn }): void {
  if (typeof auth.getUser !== 'function') return;
  const original = auth.getUser.bind(auth);
  auth.getUser = (async (...args: Parameters<GetUserFn>) => {
    const result = await original(...args);
    if (args.length === 0 && result.error && isDeletedUserError(result.error)) {
      void handleInvalidSessionError(result.error);
    }
    return result;
  }) as GetUserFn;
}

if (typeof window !== 'undefined') {
  if (import.meta.env.MODE !== 'test') watchDeletedUser(supabase.auth);
  // A fresh, valid sign-in or token refresh re-arms the guard for the next
  // stale-token event.
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      handled = false;
    }
  });
}
